import {
  CheckboxesField,
  RadiosField,
  SelectField,
  TextArea,
  TextField,
  SignatureField,
} from '../components/01_atoms/FormElements';
import Text from '../components/03_organisms/BodyBlocks/Text/BBText';
import {
  composeValidators,
  maxCharacters,
  maxValue,
  maxWords,
  minCharacters,
  minValue,
  minWords,
  mustBeNumber,
  required,
  validEmail,
} from './formFieldValidators';
import {
  composeParsers,
  parseLengthLimit,
  parseOnlyDigits,
  parseSpaceless,
} from './formFieldParsers';
import FieldsetField from '../components/01_atoms/FormElements/FieldsetField';
import CheckboxFieldWithPopup from '../components/01_atoms/FormElements/CheckboxFieldWithPopup';
import HorizontalLine from '../components/01_atoms/HorizontalLine';
import EmailIcon from '../public/static/icons/letter-icon.svg';

const debug = require('debug')('cw:transforms.webform.js');

function getForm(fields) {
  const form = {
    initialValues: {},
    components: [],
    submitButtonLabel: 'Submit',
  };

  if (!fields) {
    return form;
  }

  fields.forEach((field) => {
    let component;
    const name = `${field.field_id}`;
    const label = field.title ? `${field.title}` : '';
    const fieldProps = { label, name };

    const validators = [];
    const parsers = [];

    switch (field.type) {
      case 'textfield':
        component = TextField;

        // Add default value to initial values.
        if (field.default_value) {
          form.initialValues[name] = field.default_value;
        }
        break;

      case 'textarea':
        component = TextArea;

        // Add default value to initial values.
        if (field.default_value) {
          form.initialValues[name] = field.default_value;
        }
        break;

      case 'email':
        component = TextField;
        parsers.push(parseSpaceless);
        validators.push(validEmail());
        fieldProps.icon = <EmailIcon />;

        // Add default value to initial values.
        if (field.default_value) {
          form.initialValues[name] = field.default_value;
        }
        break;

      case 'checkbox':
        component = CheckboxesField;
        // Remove parent-level label if it's just one checkbox.
        fieldProps.label = '';
        fieldProps.options = [{ value: true, label }];
        // Add default value to initial values.
        if (field.default_value) {
          form.initialValues[name] = true;
        }
        break;

      case 'checkboxes':
        component = CheckboxesField;
        fieldProps.options = Object.keys(field.options).map((key) => ({
          value: key,
          label: `${field.options[key]}`,
        }));

        // Add default value to initial values.
        if (field.default_value) {
          form.initialValues[name] = `${field.default_value}`;
        }
        break;

      case 'select':
        component = SelectField;
        // Set a unique id for react-select.
        // See https://github.com/JedWatson/react-select/issues/2629
        fieldProps.instanceId = `webform-select-${name}`;

        fieldProps.options = Object.keys(field.options).map((key) => ({
          value: key,
          label: field.options[key],
        }));

        // Add empty option if field is not required.
        if (!field.required) {
          fieldProps.options.unshift({ value: undefined, label: '------' });
        }

        // Add default value to initial values.
        if (field.default_value && field.options[field.default_value]) {
          form.initialValues[name] = {
            value: field.default_value,
            label: field.options[field.default_value],
          };
        }
        break;

      case 'radios':
        component = RadiosField;
        fieldProps.options = Object.keys(field.options).map((key) => ({
          value: key,
          label: `${field.options[key]}`,
        }));

        // Add default value to initial values.
        if (field.default_value && field.options[field.default_value]) {
          form.initialValues[name] = field.default_value;
        }
        break;

      case 'number':
        component = TextField;
        validators.push(mustBeNumber());

        // Enable numeric keyboard on mobile.
        fieldProps.inputMode = 'numeric';
        fieldProps.pattern = '[0-9]*';

        parsers.push(parseOnlyDigits);

        if (field.max) {
          validators.push(maxValue(+field.max));
        }
        if (field.min) {
          validators.push(minValue(+field.min));
        }
        // Add default value to initial values.
        if (field.default_value) {
          form.initialValues[name] = field.default_value;
        }
        break;

      case 'webform_signature':
        component = SignatureField;

        break;

      case 'fieldset': {
        component = FieldsetField;
        const subform = getForm(field.children);

        // Add initial values to global form.
        Object.assign(form.initialValues, subform.initialValues);
        fieldProps.children = subform.components;
        fieldProps.description = field.help;
        break;
      }

      case 'webform_markup':
        component = Text;
        fieldProps.text = field.markup;
        break;

      case 'webform_horizontal_rule':
        component = HorizontalLine;
        break;

      case 'webform_terms_of_service':
        component = CheckboxFieldWithPopup;
        fieldProps.popupTitle = field.terms_title;
        fieldProps.popupHtmlContent = field.terms_content;
        // Add default value to initial values.
        if (field.default_value) {
          form.initialValues[name] = true;
        }
        break;

      case 'webform_actions':
        form.submitButtonLabel = field.submit__label;
        return;

      default:
        debug('Undefined field type %s', field.type);
        return;
    }

    if (field.required) {
      validators.push(required(field.required_error));
      fieldProps.isRequired = true;
    }

    if (field.help_title) {
      fieldProps.info = field.help_title;
    }

    // TODO: use it for the info text instead of tooltip.
    // Currently we can't do it because some webforms could
    // have configured tooltip text instead of help text.
    // if (field.help) {
    //   fieldProps.info = field.help;
    // }

    if (field.placeholder) {
      fieldProps.placeholder = field.placeholder;
    }

    // Add counter validators and parsers.
    if (field.counter_type === 'word') {
      if (field.counter_minimum) {
        validators.push(minWords(+field.counter_minimum));
      }
      if (field.counter_maximum) {
        validators.push(maxWords(+field.counter_maximum));
      }
    } else if (field.counter_type === 'character') {
      if (field.counter_maximum) {
        validators.push(maxCharacters(+field.counter_maximum));

        // Validators or parsers ???
        parsers.push(parseLengthLimit(+field.counter_maximum));
      }
      if (field.counter_minimum) {
        validators.push(minCharacters(+field.counter_minimum));
      }
    }

    // Add validators to component.
    if (validators.length) {
      fieldProps.validate = composeValidators(...validators);
    }

    // Add parsers to component.
    if (parsers.length) {
      fieldProps.parse = composeParsers(...parsers);
    }

    form.components.push({ component, props: fieldProps });
  });

  return form;
}

export default getForm;
